import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Clock, BookOpen, Sparkle, X, EnvelopeSimple, ArrowSquareOut, LinkedinLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import KoshMark from "../components/KoshMark";

const LINKEDIN_URL = "https://www.linkedin.com/company/wisewit-solutions/";

export default function Knowledge() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");

  // AI search state
  const [query, setQuery] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [sources, setSources] = useState([]);
  const [askedQuestion, setAskedQuestion] = useState("");

  // Lead-capture
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadDismissed, setLeadDismissed] = useState(false);

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
    api.get("/articles/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === "All" ? articles : articles.filter((a) => a.category === active)),
    [articles, active]
  );

  const ask = async (e) => {
    e?.preventDefault();
    if (!query.trim() || asking) return;
    setAsking(true);
    setAnswer(null); setSources([]);
    setLeadSent(false); setLeadDismissed(false); setLeadEmail("");
    setAskedQuestion(query.trim());
    try {
      const { data } = await api.post("/ai/knowledge-search", {
        question: query.trim(),
        category: active === "All" ? null : active,
      });
      setAnswer(data.answer);
      setSources(data.sources || []);
    } catch {
      setAnswer("The knowledge search is temporarily unavailable. Try the AI Scoper or Project Router.");
    } finally {
      setAsking(false);
    }
  };

  const clearAnswer = () => {
    setAnswer(null); setSources([]);
    setLeadSent(false); setLeadDismissed(false); setLeadEmail(""); setAskedQuestion("");
  };

  const submitLead = async (e) => {
    e?.preventDefault();
    if (!leadEmail.trim() || leadSubmitting) return;
    setLeadSubmitting(true);
    try {
      await api.post("/ai/knowledge-lead", {
        email: leadEmail.trim(),
        question: askedQuestion,
        answer: answer || "",
        source_titles: sources.map((s) => s.title),
      });
      setLeadSent(true);
      toast.success("Sent. Check your inbox for the discovery-call link.");
    } catch {
      toast.error("Could not send. Please try again or use the Project Router.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <div data-testid="knowledge-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="max-w-6xl">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-overline mb-4">◢ Knowledge centre</div>
            <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
              Field notes from the <span className="text-[#FF4500]">floor</span>.
            </h1>
            <p className="mt-5 text-[#8E8E93] max-w-2xl text-[15px] leading-relaxed">
              Short, technical, opinionated. Filter by the topic that matches your problem — or ask the AI directly.
            </p>
          </div>

          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="kc-linkedin-cta"
            className="ww-card px-4 py-3 hover:border-[#FF4500] transition-colors flex items-center gap-3 group"
          >
            <LinkedinLogo size={22} color="#DFFF00" weight="fill" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] group-hover:text-white">Follow on LinkedIn</div>
              <div className="font-display text-sm text-white mt-1">@wisewit-solutions</div>
            </div>
            <ArrowSquareOut size={14} className="text-[#8E8E93] group-hover:text-[#FF4500]" />
          </a>
        </div>
      </div>

      {/* KOSH grounded search */}
      <div className="mt-10 max-w-4xl">
        <form onSubmit={ask} data-testid="kc-search-form" className="flex items-center gap-3 border-b border-[#2A2A2A] focus-within:border-[#FF4500] py-1 transition-colors">
          <KoshMark size={22} tone="on-glass" />
          <input
            data-testid="kc-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask KOSH — e.g. how do we tune Bayesian setpoint optimisation?"
            className="flex-1 bg-transparent text-white text-base outline-none font-body py-3"
          />
          {answer && (
            <button type="button" onClick={clearAnswer} className="text-[#8E8E93] hover:text-white" title="Clear answer"><X size={16} /></button>
          )}
          <button data-testid="kc-search-submit" disabled={asking || !query.trim()} className="btn-primary disabled:opacity-50">
            {asking ? "KOSH is thinking…" : "Ask KOSH"}
          </button>
        </form>

        {(asking || answer) && (
          <div className="mt-6 ww-card p-6" data-testid="kc-answer">
            <div className="text-overline mb-3 flex items-center gap-2">
              <KoshMark size={14} tone="on-glass" /> KOSH · grounded answer
            </div>
            {asking ? (
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#DFFF00]">KOSH is reading the field notes…</div>
            ) : (
              <>
                <div className="text-[#F5F5F5] text-[15px] leading-relaxed whitespace-pre-wrap">{answer}</div>
                {sources.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-[#2A2A2A]">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] mb-2">Sources</div>
                    <div className="flex flex-wrap gap-2">
                      {sources.map((s) => (
                        <Link key={s.slug} to={`/knowledge/${s.slug}`} className="ww-chip hover:border-[#FF4500] transition-colors">
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lead-capture: email me + book a call */}
                {!leadDismissed && (
                  <div className="mt-6 pt-5 border-t border-dashed border-[#FF4500]/40" data-testid="kc-lead-card">
                    {leadSent ? (
                      <div className="flex items-center gap-3 text-emerald-400 text-sm">
                        <EnvelopeSimple size={18} weight="fill" />
                        <span>Sent. Check your inbox — there's a discovery-call link inside.</span>
                      </div>
                    ) : (
                      <form onSubmit={submitLead} className="flex flex-col sm:flex-row sm:items-end gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-overline mb-2 flex items-center gap-2">
                            <EnvelopeSimple size={12} weight="fill" color="#FF4500" /> Want this in your inbox + a 30-min discovery call?
                          </div>
                          <input
                            type="email"
                            required
                            data-testid="kc-lead-email"
                            value={leadEmail}
                            onChange={(e) => setLeadEmail(e.target.value)}
                            placeholder="you@operation.com"
                            className="ww-input"
                          />
                          <div className="mt-1 font-mono text-[10px] text-[#555]">We'll only email you this answer and the call link. No spam.</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="submit"
                            data-testid="kc-lead-submit"
                            disabled={leadSubmitting || !leadEmail.trim()}
                            className="btn-primary disabled:opacity-50"
                          >
                            {leadSubmitting ? "Sending…" : "Email it + book"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setLeadDismissed(true)}
                            className="btn-ghost"
                            data-testid="kc-lead-dismiss"
                          >
                            No thanks
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="knowledge-filters">
        <FilterChip label="All" active={active === "All"} onClick={() => setActive("All")} />
        {categories.map((c) => (
          <FilterChip
            key={c}
            label={c}
            active={active === c}
            onClick={() => setActive(c)}
            testid={`kc-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
          />
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-16 ww-card p-10 max-w-3xl">
          <BookOpen size={24} color="#DFFF00" weight="duotone" />
          <div className="mt-4 font-display text-2xl text-white">Nothing here yet.</div>
          <p className="mt-2 text-[#8E8E93] text-sm">
            New articles in this topic are coming. Use the AI Scoper or Project Router to ask us directly.
          </p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {filtered.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article: a }) {
  const isExternal = !!a.external_url;
  const Wrapper = isExternal ? "a" : Link;
  const wrapperProps = isExternal
    ? { href: a.external_url, target: "_blank", rel: "noreferrer" }
    : { to: `/knowledge/${a.slug}` };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`article-card-${a.slug}`}
      className="bg-[#050505] p-6 hover:bg-[#0a0a0a] transition-colors group ww-trace relative"
    >
      {a.cover_image && (
        <div className="aspect-[16/10] overflow-hidden border border-[#1A1A1A] mb-5">
          <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{a.category}</span>
        <span className="font-mono text-[10px] text-[#8E8E93] flex items-center gap-1">
          {isExternal ? (
            <>
              {(a.source || "").toLowerCase() === "linkedin"
                ? <LinkedinLogo size={11} weight="fill" />
                : <ArrowSquareOut size={11} />}
              {a.source || "External"}
            </>
          ) : (
            <><Clock size={11} /> {a.read_minutes} min</>
          )}
        </span>
      </div>
      <h3 className="mt-3 font-display text-xl text-white leading-tight">{a.title}</h3>
      <p className="mt-2 text-sm text-[#8E8E93] leading-relaxed line-clamp-3">{a.summary}</p>
      <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity">
        {isExternal ? "Open ↗" : "Read →"}
      </div>
    </Wrapper>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
        active
          ? "border-[#FF4500] text-white bg-[#FF4500]/10"
          : "border-[#2A2A2A] text-[#8E8E93] hover:text-white hover:border-[#404040]"
      }`}
    >
      {label}
    </button>
  );
}
